import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import ActivityLibrary from '../components/ActivityLibrary';
import WeeklyCompletionGrid from '../components/WeeklyCompletionGrid';
import ActivityCompletionForm from '../components/ActivityCompletionForm';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui';
import { BarChart3, Calendar } from 'lucide-react';

const ActivitiesPage = () => {
  const { user } = useAuth();
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showCompletionForm, setShowCompletionForm] = useState(false);
  const [activeTab, setActiveTab] = useState('library');

  const handleActivitySelect = (activity) => {
    setSelectedActivity(activity);
    setShowCompletionForm(true);
  };

  const handleFormClose = () => {
    setShowCompletionForm(false);
    setSelectedActivity(null);
  };

  const handleFormComplete = () => {
    handleFormClose();
    // Optionally refresh the weekly grid
    setActiveTab('grid');
  };

  return (
    <>
      <Navigation />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <Card>
          <div className="mb-8">
            <h2 className="text-3xl font-display font-semibold text-neutral-900">Wellness Activities</h2>
            <p className="mt-2 text-neutral-600">
              Build healthy habits by logging wellness-focused activities. Track your progress with our visual weekly grid.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-neutral-200 mb-8">
            <button
              onClick={() => setActiveTab('library')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'library'
                  ? 'text-primary-600 border-primary-600'
                  : 'text-neutral-600 border-transparent hover:text-neutral-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Activity Library
              </div>
            </button>
            <button
              onClick={() => setActiveTab('grid')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'grid'
                  ? 'text-primary-600 border-primary-600'
                  : 'text-neutral-600 border-transparent hover:text-neutral-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Weekly Progress
              </div>
            </button>
          </div>

          {/* Tab Content */}
          <div className="mt-6">
            {activeTab === 'library' && (
              <ActivityLibrary onActivitySelect={handleActivitySelect} />
            )}

            {activeTab === 'grid' && (
              <WeeklyCompletionGrid userId={user?.id} />
            )}
          </div>

          {/* Quick Help */}
          <div className="mt-8 rounded-lg border border-info-200 bg-info-50 p-4">
            <h4 className="font-semibold text-info-900">💡 How to Get Started</h4>
            <ul className="mt-2 space-y-1 text-sm text-info-800">
              <li>1. Browse the <strong>Activity Library</strong> to find activities that resonate with your mental wellness goals</li>
              <li>2. Click <strong>Log Activity</strong> to record when you complete an activity</li>
              <li>3. Check your <strong>Weekly Progress</strong> grid to visualize your consistency and habit building</li>
              <li>4. Consistent activity completion strengthens your mental wellness foundation and builds positive patterns</li>
            </ul>
          </div>
        </Card>

        {/* Activity Completion Form Modal */}
        {showCompletionForm && selectedActivity && (
          <ActivityCompletionForm
            activity={selectedActivity}
            onComplete={handleFormComplete}
            onClose={handleFormClose}
          />
        )}
      </main>
    </>
  );
};

export default ActivitiesPage;
